import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from '../Librarian/librarianSwal';
import { useAuth } from '../Constant/Auth';
import { useActiveLibraryId, useSectionBase } from '../Constant/useActiveLibrary';
import Loader from '../Layout/Loader';
import useMinLoading from '../Layout/useMinLoading';
import '../Librarian/Reports/ReportDashboard.css';
import '../Librarian/lib-page-header.css';

import {
  getBooksByLibrary,
  getBookCopiesByLibrary,
  getReservationsByLibrary,
  getIssuesByLibrary,
  getFinesByLibrary,
  getMembersByLibrary,
  getStudentsByLibrary,
  getAllLibrariesAdmin,
  getErrorMessage
} from '../api/api';

/**
 * School Admin dashboard — the same shape as the Librarian dashboard, but framed
 * for a single school (the admin's own `library_id`) and titled with the school
 * name. Navigation uses `useSectionBase()` so links stay inside `/school`.
 */
const SchoolDashboard = () => {
  const navigate = useNavigate();
  const sectionBase = useSectionBase();
  const { user } = useAuth();
  const libraryId = useActiveLibraryId();

  const [loading, setLoading] = useState(false);
  const showLoader = useMinLoading(loading);
  const [schoolName, setSchoolName] = useState('');
  const [stats, setStats] = useState({
    books: 0,
    copies: 0,
    availableCopies: 0,
    issuedCopies: 0,
    reservations: 0,
    pendingRequests: 0,
    activeIssues: 0,
    members: 0,
    students: 0,
    fines: 0,
    unpaidFines: 0
  });

  const fetchDashboard = async () => {
    if (!libraryId) return;
    try {
      setLoading(true);

      const [books, copies, reservations, issues, fines, members, students, libs] = await Promise.all([
        getBooksByLibrary(libraryId),
        getBookCopiesByLibrary(libraryId),
        getReservationsByLibrary(libraryId),
        getIssuesByLibrary(libraryId),
        getFinesByLibrary(libraryId),
        getMembersByLibrary(libraryId),
        getStudentsByLibrary(libraryId).catch(() => []),
        getAllLibrariesAdmin().catch(() => [])
      ]);

      const mine = (Array.isArray(libs) ? libs : []).find((l) => String(l.id) === String(libraryId));
      setSchoolName(mine?.library_name || user?.library_name || '');

      setStats({
        books: books?.length || 0,
        copies: copies?.length || 0,
        availableCopies: copies?.filter((c) => c.availability_status === 'available').length || 0,
        issuedCopies: copies?.filter((c) => c.availability_status === 'issued').length || 0,
        reservations: reservations?.length || 0,
        pendingRequests: reservations?.filter((r) => r.status === 'requested').length || 0,
        activeIssues: issues?.filter((i) => i.issue_status === 'issued').length || 0,
        members: members?.length || 0,
        students: students?.length || 0,
        fines: fines?.length || 0,
        unpaidFines: fines?.filter((f) => (f.fine_status || f.status) === 'unpaid').length || 0
      });
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryId]);

  const Card = ({ title, value, accent, path, icon }) => (
    <div
      className="report-card"
      style={{ '--accent': accent, cursor: path ? 'pointer' : 'default' }}
      onClick={() => path && navigate(path)}
    >
      {icon && (
        <span
          className="report-card__icon"
          style={{ position: 'absolute', top: 15, right: 16, fontSize: 20, color: 'var(--accent)', opacity: 0.9 }}
        >
          <i className={icon} />
        </span>
      )}
      <span className="report-card__title">{title}</span>
      <span className="report-card__value">{value}</span>
    </div>
  );

  return (
    <div className="container-fluid mt-3">
      <div className="lib-page-header">
        <div className="lib-page-header__left">
          <div className="lib-page-header__icon"><i className="fas fa-tachometer-alt" /></div>
          <div>
            <h3 className="lib-page-header__title">School Dashboard</h3>
            <div className="lib-page-header__subtitle">
              {schoolName ? schoolName : 'Overview of your school library'}
            </div>
          </div>
        </div>
      </div>

      {showLoader ? (
        <Loader />
      ) : !libraryId ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#6b7a99' }}>
          No school is linked to your account.
        </div>
      ) : (
        <>
          <div className="report-grid">
            <Card title="Books" value={stats.books} accent="#6f8fe6" path={`${sectionBase}/books`} icon="fas fa-book" />
            <Card title="Total Copies" value={stats.copies} accent="#7c5cfc" path={`${sectionBase}/book-copies`} icon="fas fa-layer-group" />
            <Card title="Available Copies" value={stats.availableCopies} accent="#22b07d" path={`${sectionBase}/book-copies`} icon="fas fa-check-circle" />
            <Card title="Issued Copies" value={stats.issuedCopies} accent="#f5a524" path={`${sectionBase}/issues`} icon="fas fa-book-reader" />
            <Card title="Parent Requests" value={stats.reservations} accent="#d6669a" path={`${sectionBase}/reservations`} icon="fas fa-inbox" />
            <Card title="Pending Requests" value={stats.pendingRequests} accent="#f59e0b" path={`${sectionBase}/reservations`} icon="fas fa-hourglass-half" />
            <Card title="Active Issues" value={stats.activeIssues} accent="#0ea5e9" path={`${sectionBase}/issues`} icon="fas fa-exchange-alt" />
            <Card title="Members" value={stats.members} accent="#2dabb5" path={`${sectionBase}/members`} icon="fas fa-users" />
            <Card title="Students" value={stats.students} accent="#14b8a6" path={`${sectionBase}/students`} icon="fas fa-user-graduate" />
            <Card title="Fines" value={stats.fines} accent="#ef5350" path={`${sectionBase}/fines`} icon="fas fa-coins" />
            <Card title="Unpaid Fines" value={stats.unpaidFines} accent="#f97362" path={`${sectionBase}/fines`} icon="fas fa-exclamation-triangle" />
          </div>

          <div className="quick-actions">
            <div className="quick-actions__head">
              <h4>Quick Actions</h4>
              <span>Common tasks for your school</span>
            </div>

            <div className="quick-actions__grid">
              <button className="qa-btn" style={{ '--qa': '#6f8fe6' }} onClick={() => navigate(`${sectionBase}/books/add`)}>
                <span className="qa-btn__icon">＋</span>
                Add Book
              </button>
              <button className="qa-btn" style={{ '--qa': '#7c5cfc' }} onClick={() => navigate(`${sectionBase}/users`)}>
                <span className="qa-btn__icon">☷</span>
                Manage Staff
              </button>
              <button className="qa-btn" style={{ '--qa': '#22b07d' }} onClick={() => navigate(`${sectionBase}/members`)}>
                <span className="qa-btn__icon">👥</span>
                Members
              </button>
              <button className="qa-btn" style={{ '--qa': '#f5a524' }} onClick={() => navigate(`${sectionBase}/announcements`)}>
                <span className="qa-btn__icon">📢</span>
                Announcements
              </button>
              <button className="qa-btn" style={{ '--qa': '#ef5350' }} onClick={() => navigate(`${sectionBase}/reports`)}>
                <span className="qa-btn__icon">📊</span>
                Reports
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SchoolDashboard;
